import { css } from '@emotion/react'
import type { RecentStudyType } from '@mathflat/domain/StudentHome'
import { memo, type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import SimpleBar from 'simplebar-react'

import useInfiniteScroll from '~/@common/hooks/useInfiniteScroll'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import usePullToRefresh from '~/@common/hooks/usePullToRefresh'
import { mediaQuery } from '~/@common/styles/mediaQuery'

import type RecentStudy from '../../@common/model/RecentStudy'
import ContentBox from '../ContentBox'
import MoreButton from '../MoreButton'
import NoData from '../NoData'
import RecentStudyTab from '../RecentStudy/RecentStudyTab'
import StudyCard from '../RecentStudy/StudyCard'
import SectionHeader from '../SectionHeader'

type Props = {
  items: RecentStudy[]
  activeType: RecentStudyType
  onChangeType: (type: RecentStudyType) => void
  /** 모바일 '더보기' 버튼 상태. */
  hasMobileMore?: boolean
  /** 모바일 '더보기' 클릭 콜백 */
  onMobileLoadMore?: () => void
  /** 데스크탑 무한스크롤 */
  onReachEnd?: () => void
  /** 데스크탑 pull-to-refresh 콜백*/
  onRefresh?: () => Promise<void> | void
  /** 섹션 헤더에 추가할 슬롯. PC 는 헤더 우측 inline, MO 는 헤더 아래 새 줄 좌측에 자동 배치 */
  headerRightSlot?: ReactNode
  /** 데이터 준비 완료 여부. false 인 상태가 SKELETON_DELAY 이상 유지되면 스켈레톤 UI 노출 */
  isReady: boolean
}

const SKELETON_LENGTH = 20
const SKELETON_DELAY = 500
const MOBILE_MIN_HEIGHT = 205
const SCROLL_CONTAINER_CLASS = 'list-container'

const StudyListView = ({
  items,
  activeType,
  onChangeType,
  hasMobileMore,
  onMobileLoadMore,
  onReachEnd,
  onRefresh,
  headerRightSlot,
  isReady,
}: Props) => {
  const { isMobile } = useStudentAppMediaQuery()
  const lastListItemRef = useRef<HTMLLIElement | null>(null)

  const [isShowSkeleton, setIsShowSkeleton] = useState(false)
  const skeletonTimerRef = useRef(0)
  useEffect(() => {
    window.clearTimeout(skeletonTimerRef.current)
    if (!isReady) {
      skeletonTimerRef.current = window.setTimeout(() => setIsShowSkeleton(true), SKELETON_DELAY)
    } else {
      setIsShowSkeleton(false)
    }
    return () => window.clearTimeout(skeletonTimerRef.current)
  }, [isReady])

  // 데스크탑 무한스크롤: sentinel 이 화면에 들어오면 onReachEnd 호출
  const handleScroll = useCallback(() => {
    if (isMobile) return
    onReachEnd?.()
  }, [isMobile, onReachEnd])
  useInfiniteScroll(lastListItemRef, handleScroll)

  usePullToRefresh({
    targetElement: `.${SCROLL_CONTAINER_CLASS}`,
    deps: [onRefresh, isShowSkeleton],
    onRefresh: async () => {
      await onRefresh?.()
    },
    shouldPullToRefreshFunc: (el) => !!onRefresh && !!el && !isMobile && el.scrollTop === 0,
  })

  const hasData = items.length > 0
  // hasMobileMore 가 undefined 이면 숨김
  const showMoreButton = isMobile && hasMobileMore !== undefined && !!onMobileLoadMore

  return (
    <section css={_SectionStyle}>
      <SectionHeader className="studylist-header">
        <RecentStudyTab activeType={activeType} onChangeType={onChangeType} />
      </SectionHeader>
      {headerRightSlot && <div className="header-sub-row">{headerRightSlot}</div>}
      {isShowSkeleton ? (
        <SimpleBar style={{ maxHeight: '100%', overflow: isMobile ? 'hidden' : 'unset' }}>
          <div
            className="content-box-wrapper"
            css={{
              backgroundColor: isMobile ? '#E6EAEE' : undefined,
              borderRadius: isMobile ? 'var(--Radius-300)' : undefined,
            }}
          >
            {!isMobile &&
              Array.from({ length: SKELETON_LENGTH }).map((_, idx) => (
                <ContentBox key={idx} isReady={false} css={{ height: 106 }} />
              ))}
          </div>
        </SimpleBar>
      ) : !isReady ? (
        // 패칭 중이지만 SKELETON_DELAY 미만 — 아무것도 렌더하지 않음
        <div className={SCROLL_CONTAINER_CLASS} />
      ) : (
        <div className={SCROLL_CONTAINER_CLASS} style={{ minHeight: hasData ? undefined : 122 }}>
          {!hasData ? (
            <NoData type={activeType} css={{ flexGrow: 1 }} />
          ) : (
            <SimpleBar style={{ maxHeight: '100%', overflow: isMobile ? 'hidden' : 'unset' }}>
              <ul className="card-list">
                {items.map((item) => (
                  <StudyCard key={`${item.type}:${item.id}`} item={item} activeType={activeType} />
                ))}
                {/* 데스크탑 무한스크롤 sentinel */}
                <li ref={lastListItemRef} className="last-item-placeholder" />
              </ul>
              {showMoreButton && <MoreButton hasMore={hasMobileMore} onClick={onMobileLoadMore} />}
            </SimpleBar>
          )}
        </div>
      )}
    </section>
  )
}

export default memo(StudyListView)

const _SectionStyle = css`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  .studylist-header {
    justify-content: space-between;
  }
  .header-sub-row {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
  }

  .content-box-wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .list-container {
    overflow: hidden;
    height: 100%;
    z-index: 0;
  }

  .card-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: relative;
  }

  .last-item-placeholder {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 10px;
    visibility: hidden;
    pointer-events: none;
  }

  ${mediaQuery.underTablet} {
    overflow: unset;
    flex: none;
    min-height: auto;

    .list-container {
      overflow: unset;
      height: auto;
      min-height: ${MOBILE_MIN_HEIGHT}px;
    }
    .content-box-wrapper {
      min-height: ${MOBILE_MIN_HEIGHT}px;
    }
  }
`
